import { useMemo } from "react";
import { useTranslation } from "react-i18next";
import { Skeleton } from "@ds/ui/skeleton";
import { Heatmap, HEATMAP_WEEKS } from "@/shared/heatmap";
import { StatRow } from "@/shared/stats";
import { grouped } from "@/app/lib/number";
import type { LogActivityDayModel, SessionLogSummaryModel } from "../logs.model";
import { sessionStats } from "../logs.trend";

/**
 * HALF A YEAR OF TRAINING DAYS, AND THE TWO NUMBERS UNDER IT.
 *
 * ═══ NO CHART LIBRARY HERE, EVEN THOUGH THE REST OF THE PRODUCT NOW HAS ONE ═══
 *
 * Every plotted chart in this app is Recharts. This is not a plot: it is a grid of coloured
 * squares and two numbers, and there is no axis, no scale and no series in it. A chart library
 * would render exactly the same `<div>`s through a renderer that exists to map values onto
 * axes. `Heatmap` stays hand-built for that reason, and the reason is its shape rather than
 * its weight.
 *
 * ═══ WHAT THE STAT CARDS SAY, AND WHAT THEY DELIBERATELY DO NOT ═══
 *
 * ⚠️ THE DESIGN'S FIRST CARD IS "PRs THIS MONTH" AND IT IS NOT BUILT. Nothing in this
 * product models a personal record: there is no `prs` table, no `is_pr` column, no field on
 * `session_logs` and no notion of one anywhere in the engine — searched across `db/schemas`,
 * `api/src` and `shared/`. A number under that label would be a definition invented in a
 * component, and the athlete would believe it. So the card counts what the wire can actually
 * answer — completed sessions this calendar month — and BUD-290's PR says why.
 */
/** Monday first, matching every week boundary in this product. */
const WEEKDAYS = ["mon", "tue", "wed", "thu", "fri", "sat", "sun"] as const;
const MONTHS = ["jan", "feb", "mar", "apr", "may", "jun", "jul", "aug", "sep", "oct", "nov", "dec"] as const;

export function SessionTrend({
  logs,
  activity,
  hasMore,
  loading,
}: {
  logs: SessionLogSummaryModel[];
  /** A year of training days. The grid fills the gaps; a day absent is a day not trained. */
  activity: LogActivityDayModel[];
  /** The service said there are more logs than the window read. Makes the total a floor. */
  hasMore: boolean;
  loading: boolean;
}) {
  const { t } = useTranslation("logs");
  const now = new Date();
  const stats = sessionStats(logs, now, hasMore);

  /* SETS PER DAY IS THE INTENSITY. A volume in kilos would be zero for a day of pull-ups,
     running and burpees, and the grid would draw a blank cell for a day somebody trained hard. */
  const days = useMemo(
    () => new Map(activity.map((day) => [day.day, day.sets])),
    [activity],
  );

  if (loading) {
    return (
      <div className="flex flex-col gap-5">
        <Skeleton className="h-24 w-full" />
        <Skeleton className="h-10 w-2/3" />
      </div>
    );
  }

  return (
    <div className="flex flex-col gap-5">
      <section className="flex flex-col gap-3">
        <h2 className="text-eyebrow text-muted-foreground uppercase">{t("history.chart.label")}</h2>
        {/*
          ═══ 🚨 A GRID OF DAYS, REPLACING THE SEVEN WEEKLY BARS ═══

          The bars answered "how often have I trained lately" over seven weeks. This answers the
          same question over half a year, in the same vertical space, and adds the one thing bars
          cannot show: WHICH days. A fortnight off at Christmas is two blank columns you see
          instantly; in a bar chart it was two short bars among seven and looked like a quiet
          month. `HEATMAP_WEEKS` argues the length.

          The two numbers below answer "how much in total", which a grid of relative shades
          deliberately does not.
        */}
        <Heatmap
          values={days}
          /* THE CAPTION COUNTS THE COLUMNS THAT ARE ACTUALLY THERE. It was hard-coded at 52 and
             would have quietly started lying the moment the window changed — which it just did. */
          label={t("history.chart.summary", { weeks: HEATMAP_WEEKS })}
          weekdayLabels={WEEKDAYS.map((key) => t(`history.weekday.${key}`))}
          monthLabels={MONTHS.map((key) => t(`history.month.${key}`))}
          lessLabel={t("history.less")}
          moreLabel={t("history.more")}
          describe={(day) =>
            day.value <= 0
              ? t("history.dayRest", { day: day.day })
              : t("history.daySets", { day: day.day, count: grouped(day.value) })
          }
        />
      </section>

      {/*
        TWO NUMBERS, IN THE ROW EVERY TRAINING APP USES FOR THEM.

        ⚠️ THEY WERE TWO BORDERED CARDS, and the box was the problem: a card puts a number at
        the same visual level as the year of training above it, so the eye had three things
        competing and no reading order. `StatRow` is type and a hairline — subordinate to the
        chart by construction, which is what a supporting number should be.
      */}
      <StatRow
        stats={[
          { value: String(stats.thisMonth), label: t("history.stat.thisMonth") },
          {
            value: stats.loggedIsFloor
              ? t("history.stat.floor", { count: stats.logged })
              : String(stats.logged),
            label: t("history.stat.logged"),
          },
        ]}
      />
    </div>
  );
}
